import type {BuilderStyle, CompRef, Pointer, Pointers, Rect} from '@wix/document-services-types'
import {type DAL, type ExtensionAPI, pointerUtils} from '@wix/document-manager-core'
import type {VariantsExtensionAPI} from '../../variants/variants'
import type {ConversionDataBuilder, PartialConversionDataBuilder} from './types'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import type {ComponentConversionData, ConversionAlgoConfig} from '../types'
import {DATA_TYPES} from '../../../constants/constants'
import _ from 'lodash'
import {createAxisAlignedEnvelope} from './utils/axisAlignedEnvelope'
import {ConversionDalNamespaces} from '../conversionDal/constants'
import type {ThemeExtAPI} from '../../theme/theme'
import {getSkinDefinition} from '../../theme/defaultValues'
import type {StyleEntryItem} from '../conversionDal/types'
import {CENTER, JUSTIFY, START} from '../plugins/alignment/constants'
import type {BuilderComponentsExtensionAPI} from '../../builderComponents/builderComponents'
import type {ManifestExtensionAPI} from '../../builderComponents/manifest'
import type {StructureExtensionAPI} from '../../structure'
import type {ComponentAlignment} from '../plugins/alignment/types'
import type {SlotsExtensionAPI} from '../../slots'
import {AlgorithmModes} from '../enums'
// eslint-disable-next-line wix-editor/no-internal-import
import {ARCHETYPE, STYLE, CSS_PROPERTIES} from '@wix/component-protocol/schema'
import type {SchemaExtensionAPI} from '../../schema/schema'
import * as constants from '../../../constants/constants'
import {builderTypes} from '../../../utils/builderUtils'
import type {FeaturesExtensionAPI} from '../../features/features'
import type {BuilderStyleExtensionAPI} from '../../builderComponents/builderStyle'
import type {InnerElementsExtensionAPI} from '../../builderComponents/innerElements'
import {deepFreeze} from '@wix/document-manager-utils'

export const enum SemanticTypes {
    TEXT = 'TEXT',
    BUTTON = 'BUTTON',
    IMAGE = 'IMAGE',
    SVG = 'SVG',
    HORIZONTAL_LINE = 'HORIZONTAL LINE',
    VERTICAL_LINE = 'VERTICAL LINE',
    BOX = 'BOX',
    SECTION = 'SECTION',
    HEADER_SECTION = 'HEADER SECTION',
    EMBEDDED_MEDIA = 'EMBEDDED MEDIA',
    INTERNAL_BOX = 'INTERNAL_BOX',
    BOX_PLUGIN = 'BOX_PLUGIN',
    UNCLASSIFIED = 'UNCLASSIFIED',
    MENU = 'MENU',
    LOGO = 'LOGO'
}

interface SemanticTypeResolvers {
    predicate(
        componentType: string,
        type: string,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ): boolean
    semanticType: SemanticTypes
}

const TEXT_COMP_TYPES = new Set([
    'wysiwyg.viewer.components.WRichText',
    'wixui.CollapsibleText',
    ARCHETYPE.Text,
    ARCHETYPE.RichTextEditor
])
const BUTTON_COMP_TYPES = new Set(['wysiwyg.viewer.components.SiteButton', 'wixui.StylableButton', ARCHETYPE.Button])
const IMAGE_COMP_TYPES = new Set(['wixui.ImageX', 'wysiwyg.viewer.components.WPhoto', ARCHETYPE.Image])
const SVG_COMP_TYPES = new Set(['wysiwyg.viewer.components.VectorImage', ARCHETYPE.VectorArt])
const MENU_COMP_TYPES = new Set([ARCHETYPE.Menu])
const LOGO_COMP_TYPES = new Set([ARCHETYPE.Logo])
const MEDIA_CONTAINER_COMP_TYPE = new Set([
    'wysiwyg.viewer.components.MediaBox',
    'wysiwyg.viewer.components.StripColumnsContainer',
    'wysiwyg.viewer.components.ClassicSection',
    'wysiwyg.viewer.components.Column',
    'wysiwyg.viewer.components.StateBoxState',
    'wysiwyg.viewer.components.StateBoxFormState',
    'wysiwyg.viewer.components.StateStripState',
    'wysiwyg.viewer.components.BoxSlideShowSlide',
    'wysiwyg.viewer.components.StripContainerSlideShowSlide',
    'wysiwyg.viewer.components.MediaContainer',
    'wysiwyg.viewer.components.MenuContainer'
])
const EMBEDDED_MEDIA_COMP_TYPES = new Set([
    'wysiwyg.viewer.components.MediaPlayer',
    'wysiwyg.viewer.components.HtmlComponent',
    'wixui.VideoPlayer',
    'wixui.CustomElementComponent',
    'wysiwyg.viewer.components.GoogleMap',
    ARCHETYPE.Map,
    ARCHETYPE.Video
])
const HORIZONTAL_LINE_COMP_TYPE = new Set(['wysiwyg.viewer.components.FiveGridLine', ARCHETYPE.Line])
const VERTICAL_LINE_COMP_TYPE = 'wysiwyg.viewer.components.VerticalLine'
const HEADER_SECTION_COMP_TYPE = 'responsive.components.HeaderSection'
const SECTION_COMP_TYPES = new Set(['responsive.components.Section', 'responsive.components.FooterSection'])
const COMP_TYPES_TO_SEMANTIC_TYPE_MAPPER: SemanticTypeResolvers[] = [
    {
        predicate: (componentType: string) => TEXT_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.TEXT
    },
    {
        predicate: (componentType: string) => BUTTON_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.BUTTON
    },
    {
        predicate: (componentType: string) => IMAGE_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.IMAGE
    },
    {
        predicate: (componentType: string) => SVG_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.SVG
    },
    {
        predicate: (componentType: string) => MENU_COMP_TYPES.has(componentType as ARCHETYPE),
        semanticType: SemanticTypes.MENU
    },
    {
        predicate: (componentType: string) => LOGO_COMP_TYPES.has(componentType as ARCHETYPE),
        semanticType: SemanticTypes.LOGO
    },
    {
        predicate: (componentType: string) => EMBEDDED_MEDIA_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.EMBEDDED_MEDIA
    },
    {
        predicate: (componentType: string) => HORIZONTAL_LINE_COMP_TYPE.has(componentType),
        semanticType: SemanticTypes.HORIZONTAL_LINE
    },
    {
        predicate: (componentType: string) => componentType === VERTICAL_LINE_COMP_TYPE,
        semanticType: SemanticTypes.VERTICAL_LINE
    },
    {
        predicate: (componentType: string) => SECTION_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.SECTION
    },
    {
        predicate: (componentType: string) => componentType === HEADER_SECTION_COMP_TYPE,
        semanticType: SemanticTypes.HEADER_SECTION
    },
    {
        predicate: (
            componentType: string,
            type: string,
            extensionAPI: ExtensionAPI,
            pointers: Pointers,
            compPointer: CompRef
        ) => {
            const {components} = extensionAPI as StructureExtensionAPI
            const parent = pointers.structure.getParent(compPointer)

            return !!(
                parent &&
                type === 'Container' &&
                [...SECTION_COMP_TYPES, HEADER_SECTION_COMP_TYPE].includes(components.getComponentType(parent)) &&
                pointers.structure.getChildren(parent)?.length === 1
            )
        },
        semanticType: SemanticTypes.INTERNAL_BOX
    },
    {
        predicate: (
            componentType: string,
            type: string,
            extensionAPI: ExtensionAPI,
            pointers: Pointers,
            compPointer: CompRef
        ) => {
            const {slots} = extensionAPI as SlotsExtensionAPI
            return slots.is.unifiedContainerPlugin(compPointer)
        },
        semanticType: SemanticTypes.INTERNAL_BOX
    },
    {
        predicate: (
            componentType: string,
            type: string,
            extensionAPI: ExtensionAPI,
            pointers: Pointers,
            compPointer: CompRef
        ) => {
            const {components} = extensionAPI as StructureExtensionAPI
            const parent = pointers.structure.getParent(compPointer)

            return !!(
                parent &&
                type === 'Container' &&
                SECTION_COMP_TYPES.has(components.getComponentType(parent)) &&
                pointers.structure.getChildren(parent)?.length === 1
            )
        },
        semanticType: SemanticTypes.INTERNAL_BOX
    },
    {
        predicate: (
            componentType: string,
            type: string,
            extensionAPI: ExtensionAPI,
            pointers: Pointers,
            compPointer: CompRef
        ) => {
            const {slots} = extensionAPI as SlotsExtensionAPI
            return slots.is.unifiedContainerPlugin(compPointer)
        },
        semanticType: SemanticTypes.INTERNAL_BOX
    },
    {
        predicate: (
            componentType: string,
            type: string,
            extensionAPI: ExtensionAPI,
            pointers: Pointers,
            compPointer: CompRef
        ) => {
            const {slots} = extensionAPI as SlotsExtensionAPI
            return slots.is.containerPlugin(compPointer)
        },
        semanticType: SemanticTypes.BOX_PLUGIN
    },
    {
        predicate: (componentType: string, type: string) => type === 'Container',
        semanticType: SemanticTypes.BOX
    }
]

const getSemanticType = (
    componentTypeOrArchType: string,
    type: string,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef
): SemanticTypes => {
    for (const {predicate, semanticType} of COMP_TYPES_TO_SEMANTIC_TYPE_MAPPER) {
        if (predicate(componentTypeOrArchType, type, extensionAPI, pointers, compPointer)) {
            return semanticType
        }
    }

    return SemanticTypes.UNCLASSIFIED
}

export const createEnvelope = (rect: Rect, degrees: number | undefined): Rect => {
    if (degrees && degrees !== 0) {
        return createAxisAlignedEnvelope(rect, degrees)
    }
    return {...rect}
}

const getMeasurements = (
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef,
    semanticType: SemanticTypes
): Rect => {
    const {variants} = extensionAPI as VariantsExtensionAPI

    if (semanticType === SemanticTypes.INTERNAL_BOX) {
        const parent = pointers.structure.getParent(compPointer)
        const parentMeasurements =
            parent && variants.getComponentItemConsideringVariants(parent as CompRef, DATA_TYPES.measurements)

        if (parentMeasurements) {
            const {width, height} = parentMeasurements
            return {width, height, x: 0, y: 0}
        }
    }

    const measurement = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.measurements)
    return _.pick(measurement, ['x', 'y', 'width', 'height']) as Rect
}

const commonBuilder: ConversionDataBuilder = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef,
    conversionAlgoConfig: ConversionAlgoConfig
): Partial<ComponentConversionData> => {
    const component = dal.get(compPointer)
    const {variants} = extensionAPI as VariantsExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const sourceLayout = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.layout)
    const targetLayout =
        conversionAlgoConfig.mode === AlgorithmModes.Adaptive
            ? variants.getComponentItemConsideringVariants(
                  pointers.components.getComponentVariantsPointer(compPointer, conversionAlgoConfig.targetVariants),
                  DATA_TYPES.layout
              )
            : undefined
    const transformations = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.transformations)
    const semanticType = getSemanticType(component.componentType, component.type, extensionAPI, pointers, compPointer)
    const measurements = getMeasurements(extensionAPI, pointers, compPointer, semanticType)

    const componentType = (extensionAPI as StructureExtensionAPI).components.getComponentType(compPointer)

    return {
        [ConversionDalNamespaces.structure]: {
            [compPointer.id]: {
                semanticType,
                componentType,
                type: component.type!,
                components: component.components,
                parent: component.parent!,
                axisAlignedEnvelope: createEnvelope(measurements, 0),
                data: dataModel.components.getItem(compPointer, DATA_TYPES.data),
                media: {
                    uri: ''
                }
            }
        },
        [ConversionDalNamespaces.measurements]: {
            [compPointer.id]: measurements
        },
        [ConversionDalNamespaces.sourceLayout]: {
            [compPointer.id]: sourceLayout
        },
        [ConversionDalNamespaces.targetLayout]: targetLayout
            ? {
                  [compPointer.id]: targetLayout
              }
            : undefined,
        [ConversionDalNamespaces.transformations]: transformations?.rotate
            ? {
                  [compPointer.id]: {rotate: transformations.rotate}
              }
            : undefined
    }
}

const getBuilderData = (extensionAPI: ExtensionAPI, compPointer: Pointer): Record<string, any> => {
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {components} = extensionAPI as StructureExtensionAPI
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const builderData = manifest.getData(compPointer, false).reduce((dataObj, {prop, value}) => {
        dataObj[prop] = value
        return dataObj
    }, {})
    const compType = components.getComponentType(compPointer)
    const dataType = builderTypes(schemaAPI, compType).data
    return schemaAPI.createItemAccordingToSchema(dataType, constants.DATA_TYPES.data, {
        data: builderData
    })
}

const getStyleEntryForComponent = (
    extensionAPI: ExtensionAPI,
    compPointer: CompRef,
    innerElementPath?: string[]
): StyleEntryItem | undefined => {
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {builderStyle} = extensionAPI as BuilderStyleExtensionAPI
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI
    const componentStyle =
        Array.isArray(innerElementPath) && innerElementPath.length > 0
            ? innerElements.getInnerElementStyle(compPointer, innerElementPath)
            : builderStyle.component.getCSSStyle(compPointer)

    if (componentStyle?.type === 'GlobalStyle') {
        return
    }

    const styleEntryItem = {
        cssProperties: {},
        cssCustomProperties: {},
        ...(componentStyle as BuilderStyle),
        metaData: Object.freeze({
            default: manifest.cssStyle.getDefaultDefinition(compPointer, innerElementPath),
            cssCustomPropertiesInfo: manifest.cssStyle.getCustomCssPropertiesRoles(compPointer, innerElementPath)
        })
    } satisfies StyleEntryItem
    Object.defineProperty(styleEntryItem, 'metaData', {
        writable: false,
        configurable: false
    })

    return styleEntryItem
}

const builderCommonBuilder: ConversionDataBuilder = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef,
    conversionAlgoConfig: ConversionAlgoConfig
): Partial<ComponentConversionData> => {
    const component = dal.get(compPointer)
    const {variants} = extensionAPI as VariantsExtensionAPI
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const sourceLayout = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.layout)
    const targetLayout =
        conversionAlgoConfig.mode === AlgorithmModes.Adaptive
            ? variants.getComponentItemConsideringVariants(
                  pointers.components.getComponentVariantsPointer(compPointer, conversionAlgoConfig.targetVariants),
                  DATA_TYPES.layout
              )
            : undefined
    const innerElementsMapIncludingRoot = manifest.innerElements.buildTraversableFlatMap(compPointer)
    const transformations = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.transformations)
    const compArchType = manifest.getArchType(component.componentType)
    const componentType = (extensionAPI as StructureExtensionAPI).components.getComponentType(compPointer)
    const finalComponentType = compArchType ?? componentType
    const semanticType = getSemanticType(compArchType, component.type, extensionAPI, pointers, compPointer)
    const compRect = getMeasurements(extensionAPI, pointers, compPointer, semanticType)

    return {
        [ConversionDalNamespaces.structure]: {
            [compPointer.id]: {
                componentType: finalComponentType,
                semanticType,
                type: component.type!,
                components: component.components,
                parent: component.parent!,
                axisAlignedEnvelope: createEnvelope(compRect, 0),
                data: getBuilderData(extensionAPI, compPointer)
            }
        },
        [ConversionDalNamespaces.measurements]: {
            [compPointer.id]: compRect
        },
        [ConversionDalNamespaces.sourceLayout]: {
            [compPointer.id]: sourceLayout
        },
        [ConversionDalNamespaces.innerElements]: innerElementsMapIncludingRoot,
        [ConversionDalNamespaces.style]: Object.fromEntries(
            Object.keys(innerElementsMapIncludingRoot)
                .map((innerElementKey: string): [string, StyleEntryItem | undefined] => [
                    innerElementKey,
                    getStyleEntryForComponent(extensionAPI, compPointer, innerElementKey.split('.').slice(1))
                ])
                .filter(([, value]) => !!value)
        ) as Record<string, StyleEntryItem>,
        [ConversionDalNamespaces.targetLayout]: targetLayout
            ? {
                  [compPointer.id]: targetLayout
              }
            : {},
        [ConversionDalNamespaces.transformations]: transformations?.rotate
            ? {
                  [compPointer.id]: {rotate: transformations.rotate}
              }
            : {}
    }
}

const getFontSizeFromTheme = (extensionAPI: ExtensionAPI, fontThemeKeyFromData?: string) => {
    const fontThemes = (extensionAPI as ThemeExtAPI).theme.getTextTheme()

    if (!fontThemeKeyFromData || !fontThemes[fontThemeKeyFromData]) {
        return
    }

    const [fontSize] = /\d+/.exec(fontThemes[fontThemeKeyFromData].fontSize) ?? []

    if (!fontSize) {
        return
    }

    return parseFloat(fontSize)
}

const getUsedFontFromSkinDefinition = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    compPointer: CompRef,
    pathToExtract: string
) => {
    const skinDefinition = getSkinDefinition(dal.get(compPointer).skin)
    const fontSizeFromSkin = _.get(skinDefinition, pathToExtract)
    const [fontThemeKeyFromData] = /font_\d+/.exec(fontSizeFromSkin) ?? []

    return getFontSizeFromTheme(extensionAPI, fontThemeKeyFromData)
}

const getMediaContainerUri = (extensionAPI: ExtensionAPI, compPointer: CompRef) => {
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const designData = dataModel.components.getItem(compPointer, DATA_TYPES.design)
    return designData?.background?.mediaRef?.uri
}

const getContainerAlignment = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef
): ComponentAlignment => {
    const children = pointers.structure.getChildren(compPointer)
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI

    return (
        children.reduce<ComponentAlignment | null>((alignment, pointer) => {
            const childStructure = dal.get(pointer)
            let rawAlignment
            const compType = (extensionAPI as StructureExtensionAPI).components.getComponentType(pointer)
            const type = (extensionAPI as StructureExtensionAPI).components.getType(pointer)
            if (builderComponents.isBuilder(compType)) {
                const semanticType = getSemanticType(
                    manifest.getArchType(childStructure.componentType),
                    type,
                    extensionAPI,
                    pointers,
                    pointer as CompRef
                )
                if (alignment === CENTER || semanticType !== SemanticTypes.TEXT) {
                    return alignment
                }
                const style = (extensionAPI as ManifestExtensionAPI).manifest.getStyle(pointer, false, [
                    {prop: STYLE.CSS_PROPERTY_TYPE.textAlign}
                ])
                if (_.isEmpty(style)) return alignment
                rawAlignment =
                    style.reduce<string | null>((finalAlignment, {value}) => {
                        const textAlignValue = value
                        if (!finalAlignment) return textAlignValue
                        if (finalAlignment !== textAlignValue) return CENTER
                        return finalAlignment
                    }, null) ?? START
            } else {
                if (alignment === CENTER || childStructure.componentType !== 'wysiwyg.viewer.components.WRichText') {
                    return alignment
                }
                const baseStyleSettings = (
                    extensionAPI as VariantsExtensionAPI
                ).variants.getComponentItemConsideringVariants(pointer, DATA_TYPES.theme)
                rawAlignment = baseStyleSettings?.style?.properties?.textAlign ?? START
            }
            const childAlignment = (rawAlignment === JUSTIFY ? undefined : rawAlignment) as ComponentAlignment
            if (childAlignment && !alignment) return childAlignment
            if (childAlignment && alignment !== childAlignment) return CENTER
            return alignment
        }, null) ?? CENTER
    )
}

const builderConversionDataBuilders: Partial<Record<SemanticTypes, PartialConversionDataBuilder>> = {
    [SemanticTypes.INTERNAL_BOX]: (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
        const alignment = getContainerAlignment(dal, extensionAPI, pointers, compPointer)
        const {layoutIntent} =
            (extensionAPI as FeaturesExtensionAPI).features.component.get(compPointer, DATA_TYPES.editorsettings) ?? {}

        return {
            [ConversionDalNamespaces.structure]: {
                [compPointer.id]: {
                    alignment,
                    layoutIntent
                }
            }
        }
    }
}

const flattenObject = (obj: Record<string, any>, separator = '.'): Record<string, any> => {
    const result: Record<string, any> = {}

    const flatten = (current: Record<string, any>, prefix = '') => {
        for (const [key, value] of Object.entries(current)) {
            const newKey = prefix ? `${prefix}${separator}${key}` : key

            if (value && typeof value === 'object' && !Array.isArray(value)) {
                flatten(value, newKey)
            } else {
                result[newKey] = value
            }
        }
    }

    flatten(obj)
    return result
}

const customConversionDataBuilders: Record<string, PartialConversionDataBuilder> = {
    'wysiwyg.viewer.components.WRichText': (
        dal: DAL,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ) => {
        const baseStyleSettings = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
            compPointer,
            DATA_TYPES.theme
        )

        return {
            [ConversionDalNamespaces.style]: {
                [compPointer.id]: {
                    cssCustomProperties: flattenObject(baseStyleSettings?.style ?? {}),
                    metaData: deepFreeze({
                        default: {
                            cssCustomProperties: {
                                'properties.textAlign': START
                            }
                        },
                        cssCustomPropertiesInfo: {
                            'properties.textAlign': CSS_PROPERTIES.CSS_PROPERTY_TYPE.textAlign,
                            'properties.fontSize': CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontSize
                        }
                    })
                }
            }
        }
    },
    'wixui.StylableButton': (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
        const baseStyleSettings = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
            compPointer,
            DATA_TYPES.theme
        )

        return {
            [ConversionDalNamespaces.style]: {
                [compPointer.id]: {
                    cssCustomProperties: flattenObject(baseStyleSettings?.style ?? {}),
                    metaData: deepFreeze({
                        default: {},
                        cssCustomPropertiesInfo: {
                            'propertiesOverride.font.fontSize': CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontSize
                        }
                    })
                }
            }
        }
    },
    'wysiwyg.viewer.components.SiteButton': (
        dal: DAL,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ) => {
        const baseStyleSettings = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
            compPointer,
            DATA_TYPES.theme
        )

        return {
            [ConversionDalNamespaces.style]: {
                [compPointer.id]: {
                    cssCustomProperties: flattenObject(baseStyleSettings?.style ?? {}),
                    metaData: deepFreeze({
                        default: {},
                        cssCustomPropertiesInfo: {
                            'propertiesOverride.fnt.fontSize': CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontSize
                        }
                    })
                }
            }
        }
    },
    'wixui.CollapsibleText': (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
        const baseStyleSettings = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
            compPointer,
            DATA_TYPES.theme
        )
        const skinFontSize = getUsedFontFromSkinDefinition(dal, extensionAPI, compPointer, 'font.defaultValue')

        return {
            [ConversionDalNamespaces.style]: {
                [compPointer.id]: {
                    cssCustomProperties: flattenObject(baseStyleSettings?.style ?? {}),
                    metaData: deepFreeze({
                        default: {
                            cssCustomProperties: {
                                'propertiesOverride.text-font.fontSize': `${skinFontSize}spx`,
                                'propertiesOverride.button-font.fontSize': `${skinFontSize}spx`
                            }
                        },
                        cssCustomPropertiesInfo: {
                            'propertiesOverride.text-font.fontSize': CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontSize,
                            'propertiesOverride.button-font.fontSize': CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontSize
                        }
                    })
                }
            }
        }
    },
    'mobile.core.components.Container': (
        dal: DAL,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ) => {
        const alignment = getContainerAlignment(dal, extensionAPI, pointers, compPointer)
        const {layoutIntent} =
            (extensionAPI as FeaturesExtensionAPI).features.component.get(compPointer, DATA_TYPES.editorsettings) ?? {}

        return {
            [ConversionDalNamespaces.structure]: {
                [compPointer.id]: {
                    alignment,
                    layoutIntent
                }
            }
        }
    },
    'responsive.components.Section': (
        dal: DAL,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ) => {
        const alignment = getContainerAlignment(dal, extensionAPI, pointers, compPointer)
        const {layoutIntent} =
            (extensionAPI as FeaturesExtensionAPI).features.component.get(compPointer, DATA_TYPES.editorsettings) ?? {}

        return {
            [ConversionDalNamespaces.structure]: {
                [compPointer.id]: {
                    alignment,
                    layoutIntent
                }
            }
        }
    },
    ...Object.fromEntries(
        Array.from(MEDIA_CONTAINER_COMP_TYPE).map((compType: string) => [
            compType,
            (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
                const uri = getMediaContainerUri(extensionAPI, compPointer)
                if (uri) {
                    return {
                        [ConversionDalNamespaces.structure]: {
                            [compPointer.id]: {
                                media: {
                                    uri
                                }
                            }
                        }
                    }
                }
            }
        ])
    )
}

export const getConversionData: ConversionDataBuilder = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef,
    conversionAlgoConfig: ConversionAlgoConfig
) => {
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
    const componentType = (extensionAPI as StructureExtensionAPI).components.getComponentType(compPointer)
    const type = (extensionAPI as StructureExtensionAPI).components.getType(compPointer)
    const isBuilder = builderComponents.isBuilder(componentType)
    const conversionDataBuilders = isBuilder
        ? _.over(
              builderCommonBuilder,
              builderConversionDataBuilders[
                  getSemanticType(manifest.getArchType(componentType), type, extensionAPI, pointers, compPointer)
              ] ?? _.constant({})
          )
        : _.over(commonBuilder, customConversionDataBuilders[componentType] ?? _.constant({}))
    return _.merge(
        {},
        ...conversionDataBuilders(
            dal,
            extensionAPI,
            pointers,
            pointerUtils.getOriginalPointerFromInner(compPointer),
            conversionAlgoConfig
        )
    )
}
